<template>
    <HamletStaticTable
        :columns="columns"
        :items="localRows"
        :striped="false"
        :bordered="true"
        empty-mode="message"
        card-class="pt-0 mt-0"
    >
        <template #header-label>
            {{ $t('Component(ResearchReviewProcess):exemptions') }}
        </template>

        <template #header-controls>
            <CommonButton
                v-if="canAddRow"
                variant="primary"
                size="sm"
                @click="addRow"
            >
                {{ $t('Component(Button):add') }}
            </CommonButton>
        </template>

        <template #cell-regulation="{ item }">
            <v-select
                :value="selectedRegulationOption(item)"
                :options="availableRegulationOptionsForRow(item)"
                label="title"
                :clearable="false"
                :append-to-body="true"
                @input="onRegulationChange(item, $event)"
            />
        </template>

        <template #cell-exemptions="{ item }">
            <v-select
                v-model="item.exemptions"
                :options="exemptionOptionsForRow(item)"
                :multiple="true"
                :close-on-select="false"
                :taggable="true"
                :push-tags="true"
                :create-option="createExemptionOption"
                :append-to-body="true"
                :clearable="false"
            />
        </template>

        <template #cell-actions="{ item }">
            <CommonButton
                variant="danger"
                size="sm"
                class="btn-icon"
                @click="removeRow(item)"
            >
                <feather-icon icon="MinusIcon" size="14"/>
            </CommonButton>
        </template>
    </HamletStaticTable>
</template>

<script>
import {HamletStaticTable, Button as CommonButton} from '@core/components/common'
import {LegislationApi} from '@core/api'
import vSelect from 'vue-select'

function clone(value) {
    return JSON.parse(JSON.stringify(value || []))
}

function isEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b)
}

function groupByRegulation(exemptions) {
    const rows = Array.isArray(exemptions) ? exemptions : []
    const map = new Map()

    rows.forEach((row) => {
        const legislationId = row && row.legislationId !== undefined && row.legislationId !== null
            ? String(row.legislationId)
            : null
        const legislationKey = row && row.legislationKey ? row.legislationKey : null
        const legislationTitle = row && row.legislation ? row.legislation : null
        const key = legislationId ? 'id:' + legislationId : 'key:' + (legislationKey || legislationTitle || '__none__')

        if (!map.has(key)) {
            map.set(key, {
                id: 'tmp-exemption-' + Date.now() + '-' + Math.random().toString(16).slice(2),
                legislationId,
                legislationKey,
                legislation: legislationTitle,
                exemptions: [],
            })
        }

        const group = map.get(key)
        const exemptionName = row && row.name ? row.name : null
        if (exemptionName && !group.exemptions.includes(exemptionName)) {
            group.exemptions.push(exemptionName)
        }
    })

    return Array.from(map.values())
}

export default {
    name: 'ExemptionsSection',
    components: {
        HamletStaticTable,
        CommonButton,
        vSelect,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            localRows: groupByRegulation(this.value),
            legislationOptions: [],
            exemptionsByLegislationKey: {},
            columns: [
                {key: 'regulation', label: this.$t('Component(RegulationTypeEnum):regulation')},
                {key: 'exemptions', label: this.$t('Component(CompositionSubstance):rohs-exemption'), thClass: 'exemptions-col-values', tdClass: 'exemptions-col-values'},
                {key: 'actions', label: '', thClass: 'text-right exemptions-col-actions', tdClass: 'text-right exemptions-col-actions'},
            ],
        }
    },
    computed: {
        selectedRegulationIds() {
            return this.localRows
                .map(row => row && row.legislationId ? String(row.legislationId) : null)
                .filter(Boolean)
        },
        selectedRegulationKeysWithoutId() {
            return this.localRows
                .filter(row => !row.legislationId)
                .map(row => row && row.legislationKey ? row.legislationKey : null)
                .filter(Boolean)
        },
        canAddRow() {
            return this.legislationOptions.length > this.localRows.length
        },
    },
    watch: {
        value: {
            deep: true,
            handler(newValue) {
                const normalized = groupByRegulation(newValue)
                if (isEqual(normalized, this.localRows)) {
                    return
                }

                this.localRows = normalized
                this.resolveRowsByLegislationMap()
            },
        },
        localRows: {
            deep: true,
            handler(newRows) {
                const emitted = this.flattenRows(newRows)
                const current = Array.isArray(this.value) ? clone(this.value) : []

                if (isEqual(emitted, current)) {
                    return
                }

                this.$emit('input', emitted)
            },
        },
    },
    created() {
        LegislationApi
            .all()
            .then((response) => {
                const rows = Array.isArray(response.data) ? response.data : []
                this.legislationOptions = rows
                    .filter(legislation => legislation && legislation.key)
                    .map(legislation => ({
                        id: legislation.id !== undefined && legislation.id !== null ? String(legislation.id) : null,
                        key: legislation.key,
                        title: legislation.title || legislation.key,
                    }))

                this.exemptionsByLegislationKey = rows.reduce((acc, legislation) => {
                    if (!legislation || !legislation.key) {
                        return acc
                    }

                    const rawExemptions = Array.isArray(legislation.exemptions) ? legislation.exemptions : []
                    acc[legislation.key] = rawExemptions
                        .map((item) => {
                            if (typeof item === 'string') {
                                return item
                            }

                            if (item && typeof item === 'object') {
                                return item.key || item.code || item.id || item.title || item.name || null
                            }

                            return null
                        })
                        .filter(Boolean)

                    return acc
                }, {})

                this.resolveRowsByLegislationMap()
            })
            .catch(() => {
                this.legislationOptions = []
                this.exemptionsByLegislationKey = {}
            })
    },
    methods: {
        nextTempId() {
            return 'tmp-exemption-' + Date.now() + '-' + Math.random().toString(16).slice(2)
        },
        addRow() {
            if (!this.canAddRow) {
                return
            }

            this.localRows.push({
                id: this.nextTempId(),
                legislationId: null,
                legislationKey: null,
                legislation: null,
                exemptions: [],
            })
        },
        removeRow(item) {
            const index = this.localRows.indexOf(item)
            if (index === -1) {
                return
            }

            this.localRows.splice(index, 1)
        },
        resolveRowsByLegislationMap() {
            if (!this.legislationOptions.length) {
                return
            }

            this.localRows.forEach((row) => {
                if (!row) {
                    return
                }

                const byId = row.legislationId
                    ? this.legislationOptions.find(option => option.id && option.id === String(row.legislationId))
                    : null

                if (byId) {
                    row.legislationId = byId.id
                    row.legislationKey = byId.key
                    row.legislation = byId.title
                    return
                }

                const byKey = row.legislationKey
                    ? this.legislationOptions.find(option => option.key === row.legislationKey)
                    : null

                if (byKey) {
                    row.legislationId = byKey.id || null
                    row.legislationKey = byKey.key
                    row.legislation = byKey.title
                }
            })
        },
        availableRegulationOptionsForRow(item) {
            const currentId = item && item.legislationId ? String(item.legislationId) : null
            const currentKey = item && item.legislationKey ? item.legislationKey : null

            return this.legislationOptions.filter((option) => {
                const idTaken = option.id && this.selectedRegulationIds.includes(option.id) && option.id !== currentId
                const keyTaken = !option.id && option.key && this.selectedRegulationKeysWithoutId.includes(option.key) && option.key !== currentKey
                return !idTaken && !keyTaken
            })
        },
        selectedRegulationOption(item) {
            if (!item) {
                return null
            }

            if (item.legislationId) {
                const byId = this.legislationOptions.find(option => option.id && option.id === String(item.legislationId))
                if (byId) {
                    return byId
                }
            }

            if (item.legislationKey) {
                const byKey = this.legislationOptions.find(option => option.key === item.legislationKey)
                if (byKey) {
                    return byKey
                }
            }

            return null
        },
        onRegulationChange(item, option) {
            if (!item) {
                return
            }

            item.legislationId = option && option.id ? option.id : null
            item.legislationKey = option && option.key ? option.key : null
            item.legislation = option && option.title ? option.title : null
            item.exemptions = []
        },
        exemptionOptionsForRow(item) {
            const key = item && item.legislationKey ? item.legislationKey : null
            if (!key) {
                return []
            }

            return Array.isArray(this.exemptionsByLegislationKey[key])
                ? this.exemptionsByLegislationKey[key]
                : []
        },
        createExemptionOption(value) {
            return (value || '').trim()
        },
        flattenRows(rows) {
            const list = Array.isArray(rows) ? rows : []
            return list.reduce((acc, row) => {
                if (!row) {
                    return acc
                }

                const names = Array.isArray(row.exemptions) ? row.exemptions : []
                names.forEach((name) => {
                    acc.push({
                        name,
                        evidence: null,
                        legislation: row.legislation || null,
                        legislationId: row.legislationId || null,
                        legislationKey: row.legislationKey || null,
                    })
                })

                return acc
            }, [])
        },
    },
}
</script>

<style scoped>
::v-deep .exemptions-col-values {
    width: 400px;
    min-width: 400px;
    max-width: 400px;
}

::v-deep .exemptions-col-actions {
    width: 80px;
    min-width: 80px;
    max-width: 80px;
}
</style>
